import React, { useCallback, useEffect, useMemo } from 'react';
import { Button, Dialog, Dialogs, Flex, Text } from '@leverege/ui-elements';
import { useWizard } from '@leverege/ui-wizard';
import { DeviceSelector, ItemDelegate } from '@leverege/molten/imagine';
import I18N from '@leverege/i18n';
import Util from "./Util.js";
import S from './SelectDevice.css';
const {
  t,
  tIcon
} = I18N.ns('pairing.imagine.SelectDevice');
export default function SelectTagDevice() {
  const {
    context,
    setHeader,
    setHeaderIcon,
    setNextDisabled,
    onChange,
    wizardData,
    wizardId
  } = useWizard();
  const {
    relationship,
    parentRelationship,
    attribute,
    tagDelegate
  } = context;
  const assetName = relationship.parentBlueprint.name;
  const tagName = relationship.blueprint.name;
  const nTagData = wizardData?.tag;
  const groupDelegate = useMemo(() => {
    const delegate = Util.getPairingDelegate(parentRelationship.path, relationship.objectType, 'pair', context);
    return delegate ?? tagDelegate;
  }, [context, tagDelegate, parentRelationship, relationship]);
  const SelectionRenderer = useMemo(() => {
    return Util.getSelectionRenderer(parentRelationship.path, relationship.objectType, 'pair');
  }, [parentRelationship, relationship]);
  useEffect(() => {
    setHeader(t('header', {
      assetName,
      tagName
    }));
    setHeaderIcon(tIcon('headerIcon'));
  }, []);
  const tag = useMemo(() => {
    let item;
    if (wizardData?.tag?.obj) {
      item = wizardData.tag.obj.id;
    }
    if (!item) {
      return null;
    }
    return ItemDelegate.getItem(item);
  }, [wizardData?.tag]);
  const onChangeDevice = useCallback(({
    data,
    value: deviceId
  }) => {
    const val = {
      id: deviceId,
      type: relationship.objectType,
      ref: tagDelegate.getInterfaceUrl(),
      data: ItemDelegate.getItem(deviceId)
    };
    onChange({
      data,
      value: val
    });
  }, []);
  const {
    relationshipEditor,
    extraEditors
  } = useMemo(() => {
    const pairingMeta = attribute?.source?.attribute?.metadata?.pairing?.pair;
    const deviceSources = pairingMeta?.tag?.deviceSources;
    let searchFields = pairingMeta?.tag?.searchFields;
    searchFields = searchFields && searchFields.length > 0 ? Util.getSearchFieldsFromAttributes(searchFields, relationship.objectType) : null;
    const relationshipEditor = /*#__PURE__*/React.createElement(DeviceSelector, {
      targetId: wizardId,
      usage: "pairing",
      value: nTagData?.obj?.id,
      eventData: "tag/obj",
      onChange: onChangeDevice,
      delegate: groupDelegate,
      relationshipPaths: deviceSources,
      itemRenderer: SelectionRenderer,
      NoResult: NoResult,
      searchFields: searchFields,
      searchHeaderTitle: t('searchHeaderTitle', {
        blueprintName: tagName
      })
    });
    const extraEditors = Util.getAttributeEditors(pairingMeta?.tag, {
      delegate: groupDelegate,
      device: tag,
      deviceFormData: nTagData,
      relationship,
      onChange,
      type: 'tag'
    });
    return {
      relationshipEditor,
      extraEditors
    };
  }, [attribute, groupDelegate, nTagData, onChange, onChangeDevice, relationship, tag, wizardId]);
  useEffect(() => {
    const pairingMeta = attribute?.source?.attribute?.metadata?.pairing?.pair?.tag;
    if (!tag) {
      setNextDisabled(true);
    } else if (pairingMeta?.extraFields) {
      const attributes = Util.getEditableAttributes(pairingMeta, relationship?.objectType, {
        requiredOnly: true
      });
      if (attributes) {
        setNextDisabled(attributes.some(attr => {
          const val = attr.get(wizardData.tag);
          return val == null;
        }));
      }
    } else {
      setNextDisabled(false);
    }
  }, [wizardData]);
  return /*#__PURE__*/React.createElement(Flex, {
    variant: "colL"
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "formTitle"
  }, t('title', {
    blueprintName: tagName
  })), /*#__PURE__*/React.createElement(Text, {
    variant: "bodySmall"
  }, t('description', {
    blueprintName: tagName
  })), relationshipEditor, extraEditors?.length > 0 && /*#__PURE__*/React.createElement("hr", {
    style: {
      width: '100%',
      height: 1,
      borderColor: 'var(--color-base200)',
      borderStyle: 'solid',
      borderBottom: 'none'
    }
  }), extraEditors);
}
function NoResult(props) {
  const {
    eventData,
    onChange,
    search
  } = props;
  const {
    context
  } = useWizard();
  const {
    AddTagForm,
    relationship,
    tagDelegate
  } = context;
  const tagName = relationship.blueprint.name;
  const canAdd = tagDelegate.canCreate();
  const onAdd = useCallback(() => {
    Dialogs.show({
      // eslint-disable-next-line react/no-unstable-nested-components
      component: ({
        show,
        onClose
      }) => {
        const onSubmitComplete = ({
          value
        }) => {
          onChange({
            data: eventData,
            value: value.id
          });
          onClose();
        };
        return /*#__PURE__*/React.createElement(Dialog, {
          show: show,
          onClose: onClose
        }, /*#__PURE__*/React.createElement(AddTagForm, {
          onSubmitComplete: onSubmitComplete,
          onCancel: onClose
        }));
      }
    });
  }, []);
  if (AddTagForm && search && canAdd) {
    return /*#__PURE__*/React.createElement(Button, {
      variant: "secondary",
      onClick: onAdd,
      className: S.add
    }, t('add', {
      name: tagName
    }));
  }
  return null;
}
SelectTagDevice.TYPE = 'imagine.pairing.SelectPairChildDevice';